import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { useSocket } from '../contexts/SocketContext';

import {
  Clock,
  XCircle,
  Search,
  Eye,
  Package,
  DollarSign,
  Bell,
  BellOff,
  Receipt,
  Printer,
  Download,
  Share2,
  Smartphone,
  Car,
  Zap,
  CalendarClock,
  FileText,
  Calendar,
  Clock4,
  MapPin,
  User,
  Phone,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';
import OrderModal from '../components/OrderModal';
import { printKotViaBluetooth } from '../utils/kotBluetoothPrint';

const Orders = () => {
  // Add print styles for KOT and Order Details
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #kot-content, #kot-content *,
        #order-details-content, #order-details-content * {
          visibility: visible;
        }
        #kot-content, #order-details-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          background: white;
        }
        .no-print {
          display: none !important;
        }
        .print-break {
          page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showKotView, setShowKotView] = useState(false);
  const [kotOrder, setKotOrder] = useState(null);
  const [bluetoothPrintingOrderId, setBluetoothPrintingOrderId] = useState(null);
  const [orderMethod, setOrderMethod] = useState('all'); // 'all', 'Instant', 'Schedule'
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const [notificationSettings, setNotificationSettings] = useState(() => {
    const saved = localStorage.getItem('notificationSettings');
    return saved
      ? JSON.parse(saved)
      : {
          sound: true,
          browser: true,
          toast: true,
        };
  });
  const [loadingStates, setLoadingStates] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();

  const {
    data: orders,
    isLoading,
    error,
  } = useQuery(
    [
      'orders',
      statusFilter,
      orderTypeFilter,
      paymentMethodFilter,
      dateRangeFilter,
    ],
    async () => {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (orderTypeFilter) params.order_type = orderTypeFilter;
      if (paymentMethodFilter) params.payment_method = paymentMethodFilter;
      if (dateRangeFilter) params.date_range = dateRangeFilter;

      const response = await api.get('/vendor/orders', { params });
      return Array.isArray(response.data) ? response.data : [];
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error('Orders query error:', error);
      },
    }
  );

  const updateOrderStatusMutation = useMutation(
    async ({ orderId, status, reason }) => {
      const payload = { status };
      if (reason) payload.reason = reason;
      const response = await api.post(`/vendor/orders/${orderId}/status`, payload);
      return response.data;
    },
    {
      onMutate: ({ orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast('Order status updated successfully!', 'order-status');
        setSelectedOrder(null);
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || 'Failed to update order status';
        showErrorToast(errorMessage, 'order-status');
        console.error(
          'Order status update error:',
          error.response?.data || error.message
        );
      },
      onSettled: (_, __, { orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    }
  );

  const approveCashOrderMutation = useMutation(
    async (orderId) => {
      const response = await api.put(`/vendor/orders/${orderId}/approve-cash`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast('Cash order approved successfully!', 'cash-approval');
        setSelectedOrder(null);
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to approve cash order',
          'cash-approval'
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    }
  );

  // New mutation for generating KOT
  const generateKotMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/vendor/orders/${orderId}/generate-kot`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: true }));
      },
      onSuccess: (data, orderId) => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast(
          `KOT generated successfully! Reference: ${data.kot_reference}`,
          'kot-generation'
        );
        setSelectedOrder(null);

        // Find the order and open KOT modal
        if (Array.isArray(orders)) {
          const order = orders.find(
            (o) => o.id.toString() === orderId.toString()
          );
          if (order) {
            const updatedOrder = {
              ...order,
              kot_reference: data.kot_reference,
            };
            setKotOrder(updatedOrder);
            setShowKotView(true);
          }
        }
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to generate KOT',
          'kot-generation'
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: false }));
      },
    }
  );

  // Filter orders based on order method
  const filteredOrders = (Array.isArray(orders) ? orders : [])
    .filter((order) => {
      // First apply search filter
      const searchMatch =
        order.id.toString().includes(searchTerm) ||
        order.user_id.toString().includes(searchTerm) ||
        order.order_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.payment_method
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (order.orderItems &&
          order.orderItems.some((item) =>
            item.menuItem?.name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
          ));

      // Then apply order method filter
      if (orderMethod === 'all') {
        return searchMatch;
      } else if (orderMethod === 'Instant') {
        return searchMatch && order.order_method === 'Instant';
      } else if (orderMethod === 'Schedule') {
        return searchMatch && order.order_method === 'Schedule';
      }
      
      return searchMatch;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'total':
          aValue = parseFloat(a.total_amount);
          bValue = parseFloat(b.total_amount);
          break;
        case 'id':
          aValue = parseInt(a.id);
          bValue = parseInt(b.id);
          break;
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }) || [];

  // Listen for real-time order updates
  useEffect(() => {
    if (!socket) return;

    const handleOrderNotification = (notification) => {
      console.log(
        '📦 Received order notification in Orders page:',
        notification
      );

      // Refresh the orders data when we receive notifications
      queryClient.invalidateQueries(['orders']);
    };

    // Subscribe to order notifications
    socket.on('order-notification', handleOrderNotification);

    return () => {
      socket.off('order-notification', handleOrderNotification);
    };
  }, [socket, queryClient]);

  // Handle URL parameters for direct order access
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderId = searchParams.get('order');
    const action = searchParams.get('action');

    if (orderId && Array.isArray(orders)) {
      const order = orders.find((o) => o.id.toString() === orderId);
      if (order) {
        setSelectedOrder(order);

        // Show action-specific toast message
        if (action === 'view') {
          showSuccessToast(`Order #${orderId} details opened`, 'order-view');
        } else if (action === 'status') {
          showSuccessToast(
            `Order #${orderId} status updated successfully`,
            'order-status'
          );
        }

        // Clear URL parameters after handling
        navigate('/orders', { replace: true });
      } else {
        showErrorToast(`Order #${orderId} not found`, 'order-not-found');
        navigate('/orders', { replace: true });
      }
    }
  }, [orders, location.search, navigate]);

  // Enhanced status update with better feedback
  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatusMutation.mutateAsync({ orderId, status });

      // Show enhanced success message
      showSuccessToast(
        `Order #${orderId} status changed to ${status}`,
        'order-status-update'
      );
    } catch (error) {
      // Enhanced error message
      showErrorToast(
        error.response?.data?.message || 'Failed to update order status',
        'order-status-update'
      );
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setOrderTypeFilter('');
    setPaymentMethodFilter('');
    setDateRangeFilter('');
    setOrderMethod('all');
    setSortBy('date');
    setSortOrder('desc');
  };

  const updateNotificationSettings = (newSettings) => {
    const updatedSettings = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updatedSettings);
    localStorage.setItem(
      'notificationSettings',
      JSON.stringify(updatedSettings)
    );
  };

  const hasActiveFilters =
    searchTerm ||
    statusFilter ||
    orderTypeFilter ||
    paymentMethodFilter ||
    dateRangeFilter ||
    orderMethod !== 'all';

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval':
        return 'bg-orange-100 text-orange-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'preparing':
        return 'bg-orange-100 text-orange-800';
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'out_for_delivery':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getNextStatus = (currentStatus, paymentMethod, orderType) => {
    const isDelivery = orderType === 'delivery';
    if (isDelivery) {
      switch (currentStatus) {
        case 'pending':
          return 'confirmed';
        case 'pending_approval':
          return 'confirmed';
        case 'confirmed':
          return 'preparing';
        case 'preparing':
          return 'out_for_delivery';
        case 'out_for_delivery':
          return null;
        default:
          return null;
      }
    }
    switch (currentStatus) {
      case 'pending':
        return 'confirmed';
      case 'pending_approval':
        return 'confirmed';
      case 'confirmed':
        return 'preparing';
      case 'preparing':
        return 'ready';
      case 'ready':
        return 'completed';
      default:
        return null;
    }
  };

  // Add this function after getNextStatus (around line 419)
  const formatStatusLabel = (status) => {
    const statusLabels = {
      pending: 'Pending',
      pending_approval: 'Pending Approval',
      confirmed: 'Confirmed',
      preparing: 'Preparing',
      packed: 'Packed',
      ready: 'Ready',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      delayed: 'Delayed',
    };
    return (
      statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  const getStatusFlow = (order) => {
    const isDelivery = order.order_type === 'delivery';
    const statusFlow = isDelivery
      ? [
          {
            status: 'pending',
            label: 'Pending',
            color: 'bg-yellow-100 text-yellow-800',
            icon: '⏳',
          },
          {
            status: 'pending_approval',
            label: 'Pending Approval',
            color: 'bg-orange-100 text-orange-800',
            icon: '🔍',
          },
          {
            status: 'confirmed',
            label: 'Confirmed',
            color: 'bg-blue-100 text-blue-800',
            icon: '✅',
          },
          {
            status: 'preparing',
            label: 'Preparing',
            color: 'bg-purple-100 text-purple-800',
            icon: '👨‍🍳',
          },
          {
            status: 'out_for_delivery',
            label: 'Out for Delivery',
            color: 'bg-indigo-100 text-indigo-800',
            icon: '🚚',
          },
          {
            status: 'delivered',
            label: 'Delivered',
            color: 'bg-green-100 text-green-800',
            icon: '✅',
          },
          {
            status: 'cancelled',
            label: 'Cancelled',
            color: 'bg-red-100 text-red-800',
            icon: '❌',
          },
        ]
      : [
          {
            status: 'pending',
            label: 'Pending',
            color: 'bg-yellow-100 text-yellow-800',
            icon: '⏳',
          },
          {
            status: 'pending_approval',
            label: 'Pending Approval',
            color: 'bg-orange-100 text-orange-800',
            icon: '🔍',
          },
          {
            status: 'confirmed',
            label: 'Confirmed',
            color: 'bg-blue-100 text-blue-800',
            icon: '✅',
          },
          {
            status: 'preparing',
            label: 'Preparing',
            color: 'bg-purple-100 text-purple-800',
            icon: '👨‍🍳',
          },
          {
            status: 'ready',
            label: 'Ready',
            color: 'bg-green-100 text-green-800',
            icon: '🎉',
          },
          {
            status: 'completed',
            label: 'Completed',
            color: 'bg-green-100 text-green-800',
            icon: '✅',
          },
          {
            status: 'cancelled',
            label: 'Cancelled',
            color: 'bg-red-100 text-red-800',
            icon: '❌',
          },
        ];

    const currentIndex = statusFlow.findIndex((s) => s.status === order.status);
    const nextStatus = getNextStatus(
      order.status,
      order.payment_method,
      order.order_type
    );
    const nextIndex = statusFlow.findIndex((s) => s.status === nextStatus);

    return {
      flow: statusFlow,
      currentIndex,
      nextIndex,
      nextStatus,
      canProgress: nextIndex > currentIndex,
      canCancel:
        ['pending', 'pending_approval', 'confirmed', 'preparing', 'packed', 'delayed', 'ready'].includes(order.status),
    };
  };

  const getStatusAction = (order) => {
    const statusFlow = getStatusFlow(order);

    if (!statusFlow.nextStatus) return null;

    if (
      order.status === 'pending_approval' &&
      order.payment_method === 'cash_at_counter'
    ) {
      return {
        label: 'Approve Cash Order',
        action: () => approveCashOrderMutation.mutate(order.id),
        loading: approveCashOrderMutation.isLoading,
        color: 'bg-green-600 hover:bg-green-700',
        icon: '💵',
      };
    }

    return {
      label: `Mark as ${formatStatusLabel(statusFlow.nextStatus)}`,
      action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
      loading: updateOrderStatusMutation.isLoading,
      color: 'bg-primary-600 hover:bg-primary-700',
      icon: '➡️',
    };
  };

  // New function for inline status changes
  const getInlineStatusActions = (order) => {
    const statusFlow = getStatusFlow(order);
    const actions = [];
    const isOrderLoading = loadingStates[order.id] || false;

    // Add next status action
    if (statusFlow.nextStatus) {
      if (
        order.status === 'pending_approval' &&
        order.payment_method === 'cash_at_counter'
      ) {
        actions.push({
          label: 'Approve',
          action: () => approveCashOrderMutation.mutate(order.id),
          loading: isOrderLoading,
          color: 'bg-green-600 hover:bg-green-700',
          icon: '💵',
          size: 'sm',
        });
      } else {
        actions.push({
          label: formatStatusLabel(statusFlow.nextStatus),
          action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
          loading: isOrderLoading,
          color: 'bg-primary-600 hover:bg-primary-700',
          icon: '➡️',
          size: 'sm',
        });
      }
    }

    // Special case: Show disabled "Delivered" button for delivery orders that are "out_for_delivery"
    if (
      order.order_type === 'delivery' &&
      order.status === 'out_for_delivery'
    ) {
      actions.push({
        label: 'Delivered',
        action: null,
        loading: false,
        color: 'bg-gray-400 cursor-not-allowed opacity-50',
        icon: '✅',
        size: 'sm',
        disabled: true,
      });
    }

    // Add cancel action for early stages
    if (statusFlow.canCancel) {
      actions.push({
        label: 'Cancel',
        action: () => {
          setCancelOrderId(order.id);
          setCancelReason('');
          setShowCancelModal(true);
        },
        loading: isOrderLoading,
        color: 'bg-red-600 hover:bg-red-700',
        icon: '❌',
        size: 'sm',
      });
    }

    return actions;
  };

  // New function for KOT actions
  const getKotActions = (order) => {
    const actions = [];
    const isKotLoading = loadingStates[`kot-${order.id}`] || false;

    // Check for KOT reference in multiple possible locations
    const kotReference =
      order.kot_reference || order.kotReference || order.kot?.reference;

    if (kotReference) {
      // View KOT action
      actions.push({
        label: 'View KOT',
        action: () => {
          console.log(
            'Opening KOT for order:',
            order.id,
            'KOT ref:',
            kotReference
          );
          setKotOrder(order);
          setShowKotView(true);
        },
        color: 'bg-green-600 hover:bg-green-700',
        icon: '📋',
        size: 'sm',
      });
    } else {
      // Generate KOT action - now opens KOT modal after generation
      actions.push({
        label: 'Generate KOT',
        action: () => {
          if (window.confirm(`Generate KOT for order #${order.id}?`)) {
            generateKotMutation.mutate(order.id);
          }
        },
        loading: isKotLoading,
        color: 'bg-blue-600 hover:bg-blue-700',
        icon: '🖨️',
        size: 'sm',
      });
    }

    return actions;
  };

  // Function to handle scheduled order download (Excel/CSV)
  const handleScheduledOrderDownload = () => {
    const scheduledOrders = filteredOrders.filter(order => order.order_method === 'Schedule');
    
    if (scheduledOrders.length === 0) {
      showErrorToast('No scheduled orders to download', 'scheduled-download');
      return;
    }

    // Create CSV with detailed information
    const headers = [
      'Order ID',
      'Customer ID',
      'Order Type',
      'Order Status',
      'Order Method',
      'Scheduled Date',
      'Scheduled Time',
      'Created At',
      'Total Amount',
      'Subtotal',
      'Tax',
      'Delivery Fee',
      'Payment Method',
      'Payment Status',
      'Transaction ID',
      'Delivery Address',
      'Delivery Instructions',
      'Vehicle Make',
      'Vehicle Colour',
      'Vehicle Registration',
      'Allergy Note',
      'KOT Reference',
      'Order Items Count',
      'Order Items Details'
    ];

    const csvContent = [
      headers.join(','),
      ...scheduledOrders.map(order => {
        // Format order items details
        const orderItemsDetails = order.orderItems && order.orderItems.length > 0 
          ? order.orderItems.map(item => {
              const itemName = item.menuItem?.name || `Item #${item.menu_item_id}`;
              const addons = item.addons && item.addons.length > 0 
                ? item.addons.map(addon => 
                    `${addon.quantity}x ${addon.menuItemAddon?.name || 'Addon'}: £${addon.price_at_time}`
                  ).join('; ')
                : 'None';
              return `${item.quantity}x ${itemName} @ £${item.price} (Addons: ${addons})`;
            }).join(' | ')
          : 'No items';

        // Get vehicle data
        const vehicleData = order.vehicle_data || {};
        
        // Get delivery address
        const deliveryAddress = order.delivery_address || 
                               order.address || 
                               (order.user && order.user.address) || 
                               'N/A';

        return [
          order.id,
          order.user_id,
          order.order_type,
          order.status,
          order.order_method,
          order.scheduled_date ? new Date(order.scheduled_date).toLocaleDateString() : 'N/A',
          order.scheduled_time || 'N/A',
          new Date(order.created_at).toLocaleString(),
          `£${order.total_amount}`,
          `£${order.subtotal || order.total_amount}`,
          `£${order.tax || '0.00'}`,
          `£${order.delivery_fee || '0.00'}`,
          order.payment_method === 'cash_at_counter' ? 'Cash' : 'Card',
          order.payment_status,
          order.transaction_id || 'N/A',
          `"${deliveryAddress.replace(/"/g, '""')}"`, // Escape quotes for CSV
          order.delivery_instructions || 'N/A',
          vehicleData.make || 'N/A',
          vehicleData.colour || 'N/A',
          vehicleData.registrationNumber || 'N/A',
          order.allergy_note || 'N/A',
          order.kot_reference || order.kotReference || order.kot?.reference || 'N/A',
          order.orderItems?.length || 0,
          `"${orderItemsDetails.replace(/"/g, '""')}"` // Escape quotes for CSV
        ].join(',');
      })
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Scheduled_Orders_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showSuccessToast(`Downloaded ${scheduledOrders.length} scheduled orders as CSV`, 'scheduled-download');
  };

  // Function to handle scheduled order print (HTML/PDF)
  const handleScheduledOrderPrint = () => {
    const scheduledOrders = filteredOrders.filter(order => order.order_method === 'Schedule');
    
    if (scheduledOrders.length === 0) {
      showErrorToast('No scheduled orders to print', 'scheduled-print');
      return;
    }

    // Calculate totals
    const totalAmount = scheduledOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
    const scheduledToday = scheduledOrders.filter(order => {
      if (!order.scheduled_date) return false;
      const scheduledDate = new Date(order.scheduled_date);
      const today = new Date();
      return scheduledDate.toDateString() === today.toDateString();
    }).length;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Scheduled Orders Report</title>
        <style>
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            margin: 20px; 
            line-height: 1.6;
            color: #333;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 3px solid #4CAF50; 
            padding-bottom: 20px; 
          }
          .header h1 { 
            margin: 0 0 10px 0; 
            color: #2c3e50; 
            font-size: 32px;
          }
          .header p { 
            margin: 5px 0; 
            color: #7f8c8d;
            font-size: 14px;
          }
          .stats { 
            display: flex; 
            justify-content: space-around; 
            margin: 20px 0; 
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            padding: 20px;
            border-radius: 10px;
            border: 1px solid #ddd;
          }
          .stat-item { 
            text-align: center; 
            padding: 15px;
            min-width: 150px;
          }
          .stat-value { 
            font-size: 28px; 
            font-weight: bold; 
            color: #2c3e50;
            margin-bottom: 5px;
          }
          .stat-label { 
            font-size: 14px; 
            color: #7f8c8d;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          th { 
            background-color: #4CAF50; 
            color: white;
            padding: 15px 12px; 
            text-align: left; 
            border-bottom: 2px solid #388E3C;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 13px;
          }
          td { 
            padding: 12px; 
            border-bottom: 1px solid #e0e0e0;
            vertical-align: top;
          }
          tr:hover {
            background-color: #f9f9f9;
          }
          .total-row { 
            font-weight: bold; 
            background-color: #f9f9f9; 
            border-top: 2px solid #ddd;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-align: center;
            min-width: 80px;
          }
          .status-pending { background-color: #fef3c7; color: #92400e; }
          .status-confirmed { background-color: #dbeafe; color: #1e40af; }
          .status-preparing { background-color: #f3e8ff; color: #6b21a8; }
          .status-ready { background-color: #d1fae5; color: #065f46; }
          .status-completed { background-color: #d1fae5; color: #065f46; }
          .status-cancelled { background-color: #fee2e2; color: #991b1b; }
          .badge-instant { background-color: #fef3c7; color: #92400e; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
          .badge-schedule { background-color: #dbeafe; color: #1e40af; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
          .payment-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            background-color: #e0e7ff;
            color: #3730a3;
          }
          .amount-cell {
            font-weight: bold;
            color: #2c3e50;
          }
          .date-cell {
            font-size: 12px;
            color: #7f8c8d;
          }
          .items-cell {
            font-size: 12px;
            color: #666;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .summary {
            margin-top: 30px;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 10px;
            border-left: 4px solid #4CAF50;
          }
          .summary h3 {
            margin-top: 0;
            color: #2c3e50;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ecf0f1;
            color: #7f8c8d;
            font-size: 12px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
            .header { border-color: #000; }
            table { box-shadow: none; }
            .stats { background: #f5f5f5; }
          }
          @page {
            margin: 1cm;
            size: A4 landscape;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Scheduled Orders Report</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
        
        <div class="stats">
          <div class="stat-item">
            <div class="stat-value">${scheduledOrders.length}</div>
            <div class="stat-label">Total Orders</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${scheduledToday}</div>
            <div class="stat-label">Scheduled Today</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">£${totalAmount.toFixed(2)}</div>
            <div class="stat-label">Total Value</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">
              ${scheduledOrders.filter(o => o.status === 'pending').length}
            </div>
            <div class="stat-label">Pending Orders</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer ID</th>
              <th>Order Type</th>
              <th>Scheduled Date</th>
              <th>Scheduled Time</th>
              <th>Status</th>
              <th>Total Amount</th>
              <th>Payment Method</th>
              <th>Created At</th>
              <th>Items</th>
            </tr>
          </thead>
          <tbody>
            ${scheduledOrders.map(order => `
              <tr>
                <td><strong>#${order.id}</strong></td>
                <td>${order.user_id}</td>
                <td>
                  ${order.order_type}
                  <br>
                  <span class="badge-schedule">Scheduled</span>
                </td>
                <td>
                  ${order.scheduled_date ? new Date(order.scheduled_date).toLocaleDateString() : 'N/A'}
                </td>
                <td><strong>${order.scheduled_time || 'N/A'}</strong></td>
                <td>
                  <span class="status-badge status-${order.status}">
                    ${order.status}
                  </span>
                </td>
                <td class="amount-cell">£${order.total_amount}</td>
                <td>
                  <span class="payment-badge">
                    ${order.payment_method === 'cash_at_counter' ? 'Cash' : 'Card'}
                  </span>
                </td>
                <td class="date-cell">${new Date(order.created_at).toLocaleString()}</td>
                <td class="items-cell">
                  ${order.orderItems && order.orderItems.length > 0 
                    ? order.orderItems.map(item => 
                        `${item.quantity}x ${item.menuItem?.name || 'Item'}`
                      ).join(', ') 
                    : 'No items'}
                  ${order.orderItems && order.orderItems.length > 2 
                    ? ` (+${order.orderItems.length - 2} more)` 
                    : ''}
                </td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="6" style="text-align: right;"><strong>Total:</strong></td>
              <td><strong>£${totalAmount.toFixed(2)}</strong></td>
              <td colspan="3"><strong>${scheduledOrders.length} orders</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div class="summary">
          <h3>📊 Summary</h3>
          <p><strong>Report Period:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>Total Scheduled Orders:</strong> ${scheduledOrders.length}</p>
          <p><strong>Order Status Breakdown:</strong></p>
          <ul>
            <li>Pending: ${scheduledOrders.filter(o => o.status === 'pending').length}</li>
            <li>Confirmed: ${scheduledOrders.filter(o => o.status === 'confirmed').length}</li>
            <li>Preparing: ${scheduledOrders.filter(o => o.status === 'preparing').length}</li>
            <li>Ready: ${scheduledOrders.filter(o => o.status === 'ready').length}</li>
            <li>Completed: ${scheduledOrders.filter(o => o.status === 'completed').length}</li>
          </ul>
          <p><strong>Total Revenue:</strong> £${totalAmount.toFixed(2)}</p>
        </div>
        
        <div class="footer">
          <p>Generated by Restaurant POS System • Page 1 of 1</p>
          <p>${new Date().toLocaleString()}</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 500);
          }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Function to handle order PDF download
  const handleOrderPDFDownload = (order) => {
    handleOrderDetailsPrintDownload(order, 'pdf');
  };

  // Function to handle order details print/download
  const handleOrderDetailsPrintDownload = (order, action) => {
    // Get delivery address from order data
    const deliveryAddress = order.delivery_address || 
                           order.address || 
                           (order.user && order.user.address) || 
                           'No delivery address provided';

    // Format order items with addons
    const orderItemsHtml = order.orderItems && order.orderItems.length > 0 ? order.orderItems.map((item, index) => {
      const addonsHtml = item.addons && item.addons.length > 0 ? `
        <div style="margin-left: 20px; margin-top: 5px; border-left: 2px solid #4CAF50; padding-left: 10px;">
          <strong>Add-ons:</strong>
          <ul style="margin: 5px 0; padding-left: 15px;">
            ${item.addons.map(addon => `
              <li>${addon.quantity}x ${addon.menuItemAddon?.name || `Addon #${addon.menu_item_addon_id}`} - £${addon.price_at_time}</li>
            `).join('')}
          </ul>
        </div>
      ` : '';

      return `
        <div style="margin-bottom: 15px; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <strong>${item.quantity}x ${item.menuItem?.name || `Item #${item.menu_item_id}`}</strong>
              <p style="margin: 5px 0 0 0; color: #666;">£${item.price} each</p>
            </div>
            <div style="font-weight: bold; font-size: 1.1em;">
              £${(item.quantity * parseFloat(item.price)).toFixed(2)}
            </div>
          </div>
          ${addonsHtml}
          ${item.special_instructions ? `
            <div style="margin-top: 5px; padding: 5px; background-color: #fff8e1; border-radius: 3px; font-size: 0.9em;">
              <strong>Special Instructions:</strong> ${item.special_instructions}
            </div>
          ` : ''}
          ${item.allergy_information ? `
            <div style="margin-top: 5px; padding: 5px; background-color: #ffebee; border-radius: 3px; font-size: 0.9em; color: #c62828;">
              <strong>⚠️ Allergy Info:</strong> ${item.allergy_information}
            </div>
          ` : ''}
        </div>
      `;
    }).join('') : '<p>No items found</p>';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order Details #${order.id}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #4CAF50; padding-bottom: 20px; }
          .header h1 { margin: 0; color: #2c3e50; font-size: 28px; }
          .header p { margin: 5px 0; color: #7f8c8d; }
          .info-section { margin-bottom: 25px; }
          .info-section h2 { color: #2c3e50; border-bottom: 2px solid #ecf0f1; padding-bottom: 8px; margin-bottom: 15px; font-size: 20px; }
          .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
          .grid-item { background-color: #f9f9f9; padding: 15px; border-radius: 8px; border: 1px solid #ecf0f1; }
          .grid-item h3 { margin-top: 0; color: #34495e; font-size: 16px; }
          .badge { display: inline-block; padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; margin-right: 8px; margin-bottom: 8px; }
          .badge-instant { background-color: #fef3c7; color: #92400e; }
          .badge-schedule { background-color: #dbeafe; color: #1e40af; }
          .badge-pending { background-color: #fef3c7; color: #92400e; }
          .badge-confirmed { background-color: #dbeafe; color: #1e40af; }
          .badge-preparing { background-color: #f3e8ff; color: #6b21a8; }
          .badge-ready { background-color: #d1fae5; color: #065f46; }
          .badge-completed { background-color: #d1fae5; color: #065f46; }
          .badge-delivered { background-color: #d1fae5; color: #065f46; }
          .badge-cancelled { background-color: #fee2e2; color: #991b1b; }
          .item-card { margin-bottom: 15px; padding: 15px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #fff; }
          .item-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
          .item-name { font-weight: bold; font-size: 16px; }
          .item-price { font-weight: bold; font-size: 16px; color: #4CAF50; }
          .addons-section { margin-left: 20px; margin-top: 10px; padding-left: 15px; border-left: 2px solid #4CAF50; }
          .addon-item { margin-bottom: 5px; font-size: 14px; }
          .instructions { margin-top: 10px; padding: 10px; background-color: #fff8e1; border-radius: 5px; font-size: 14px; }
          .allergy-warning { margin-top: 10px; padding: 10px; background-color: #ffebee; border-radius: 5px; color: #c62828; font-size: 14px; }
          .total-section { margin-top: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 10px; border: 2px solid #4CAF50; }
          .total-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .total-amount { font-size: 24px; font-weight: bold; color: #2c3e50; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ecf0f1; color: #7f8c8d; font-size: 12px; }
          .address-section { background-color: #e8f5e9; padding: 15px; border-radius: 8px; border-left: 4px solid #4CAF50; }
          .address-icon { display: inline-block; margin-right: 8px; color: #4CAF50; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
            .header { border-color: #000; }
            .total-section { border-color: #000; }
          }
          .print-break { page-break-inside: avoid; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ORDER DETAILS</h1>
          <p>Order #${order.id} | Generated: ${new Date().toLocaleString()}</p>
          <div style="margin-top: 10px;">
            <span class="badge badge-${order.order_method === 'Instant' ? 'instant' : 'schedule'}">
              ${order.order_method === 'Instant' ? '⚡ Instant' : '📅 Scheduled'}
            </span>
            <span class="badge badge-${order.status}">${order.status.toUpperCase()}</span>
            ${order.order_type === 'delivery' ? '<span class="badge">🚚 Delivery</span>' : ''}
            ${order.order_type === 'takeaway' ? '<span class="badge">📦 Takeaway</span>' : ''}
            ${order.order_type === 'dine_in' ? '<span class="badge">🍽️ Dine-in</span>' : ''}
          </div>
        </div>

        <div class="grid-2">
          <div class="grid-item">
            <h3>📅 Order Information</h3>
            <p><strong>Order ID:</strong> #${order.id}</p>
            <p><strong>Order Date:</strong> ${new Date(order.created_at).toLocaleString()}</p>
            <p><strong>Order Type:</strong> ${order.order_type.replace('_', ' ').toUpperCase()}</p>
            <p><strong>Order Method:</strong> ${order.order_method}</p>
            ${order.order_method === 'Schedule' && order.scheduled_time ? `
              <p><strong>Scheduled Time:</strong> ${order.scheduled_time}</p>
              ${order.scheduled_date ? `<p><strong>Scheduled Date:</strong> ${new Date(order.scheduled_date).toLocaleDateString()}</p>` : ''}
            ` : ''}
          </div>

          <div class="grid-item">
            <h3>👤 Customer Information</h3>
            <p><strong>Customer ID:</strong> ${order.user_id}</p>
            <p><strong>Payment Method:</strong> ${order.payment_method === 'cash_at_counter' ? '💵 Cash at Counter' : '💳 Card Payment'}</p>
            <p><strong>Payment Status:</strong> <span style="color: ${order.payment_status === 'paid' ? '#4CAF50' : '#FF9800'}">${order.payment_status.toUpperCase()}</span></p>
            ${order.transaction_id ? `<p><strong>Transaction ID:</strong> ${order.transaction_id}</p>` : ''}
          </div>
        </div>

        ${order.order_type === 'delivery' ? `
          <div class="info-section print-break">
            <h2>📍 Delivery Address</h2>
            <div class="address-section">
              <p><span class="address-icon">📍</span> ${deliveryAddress}</p>
              ${order.delivery_instructions ? `
                <div style="margin-top: 10px; padding: 10px; background-color: #e3f2fd; border-radius: 5px;">
                  <strong>Delivery Instructions:</strong> ${order.delivery_instructions}
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}

        ${order.vehicle_data ? `
          <div class="info-section">
            <h2>🚗 Vehicle Information</h2>
            <div class="grid-2">
              <div class="grid-item">
                <p><strong>Make:</strong> ${order.vehicle_data.make || 'N/A'}</p>
                <p><strong>Colour:</strong> ${order.vehicle_data.colour || 'N/A'}</p>
              </div>
              <div class="grid-item">
                <p><strong>Registration:</strong> ${order.vehicle_data.registrationNumber || 'N/A'}</p>
              </div>
            </div>
          </div>
        ` : ''}

        <div class="info-section print-break">
          <h2>📋 Order Items (${order.orderItems?.length || 0})</h2>
          ${orderItemsHtml}
        </div>

        ${order.allergy_note ? `
          <div class="info-section">
            <h2>⚠️ Allergy Information</h2>
            <div style="padding: 15px; background-color: #ffebee; border-radius: 8px; color: #c62828; border-left: 4px solid #f44336;">
              ${order.allergy_note}
            </div>
          </div>
        ` : ''}

        <div class="total-section print-break">
          <h2 style="margin-top: 0; text-align: center;">💰 Order Summary</h2>
          <div class="total-row">
            <span>Subtotal:</span>
            <span>£${order.subtotal || order.total_amount}</span>
          </div>
          <div class="total-row">
            <span>Tax:</span>
            <span>£${order.tax || '0.00'}</span>
          </div>
          <div class="total-row">
            <span>Delivery Fee:</span>
            <span>£${order.delivery_fee || '0.00'}</span>
          </div>
          <div class="total-row" style="margin-top: 15px; padding-top: 15px; border-top: 2px solid #ddd;">
            <span class="total-amount">TOTAL AMOUNT:</span>
            <span class="total-amount">£${order.total_amount}</span>
          </div>
        </div>

        <div class="footer">
          <p>Generated by Restaurant POS System</p>
          <p>${new Date().toLocaleString()}</p>
        </div>

        <script>
          window.onload = function() {
            ${action === 'print' ? 'window.print();' : ''}
            ${action === 'print' ? 'setTimeout(function() { window.close(); }, 500);' : ''}
          }
        </script>
      </body>
      </html>
    `;

    if (action === 'download') {
      // Create downloadable HTML file
      const blob = new Blob([printContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Order_${order.id}_Details.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccessToast('Order details downloaded as HTML', 'order-download');
    } else if (action === 'print') {
      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
    } else if (action === 'pdf') {
      // For PDF, we'll use the browser's print to PDF functionality
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  // Function to handle print for individual order
  const handleOrderPrint = (order) => {
    handleOrderDetailsPrintDownload(order, 'print');
  };

  // Generate plain text KOT for Bluetooth printing
  const getKotText = (order) => {
    const kotRef = order.kot_reference || order.kotReference || order.kot?.reference || 'N/A';
    const lines = [];
    lines.push('JUICE PLANET');
    lines.push('================================');
    lines.push(`KOT: ${kotRef}`);
    lines.push(`Order #${order.id}`);
    lines.push(`Date: ${new Date(order.created_at).toLocaleString()}`);
    lines.push(`Type: ${(order.order_type || 'takeaway').toUpperCase()}`);
    lines.push(`Payment: ${order.payment_method === 'cash_at_counter' ? 'Cash' : 'Card'}`);
    lines.push('================================');
    lines.push('ITEMS:');
    lines.push('--------------------------------');
    if (order.orderItems) {
      order.orderItems.forEach((item) => {
        const name = item.menuItem?.name || `Item #${item.menu_item_id}`;
        lines.push(`${item.quantity}x ${name}  £${item.price}`);
        if (item.addons && item.addons.length > 0) {
          item.addons.forEach((addon) => {
            const addonName = addon.menuItemAddon?.name || `Addon #${addon.menu_item_addon_id}`;
            lines.push(`  + ${addon.quantity}x ${addonName}  £${addon.price_at_time}`);
          });
        }
      });
    }
    if (order.delivery_instructions) {
      lines.push('--------------------------------');
      lines.push(`NOTE: ${order.delivery_instructions}`);
    }
    if (order.allergy_note) {
      lines.push('--------------------------------');
      lines.push(`ALLERGY: ${order.allergy_note}`);
    }
    lines.push('================================');
    lines.push(`TOTAL: £${order.total_amount}`);
    lines.push('================================');
    lines.push('Powered by KnockIt Technology');
    return lines.join('\n');
  };

  // Handle Bluetooth KOT printing
  const handleOrderPrintBluetooth = async (order) => {
    if (bluetoothPrintingOrderId !== null) return;
    setBluetoothPrintingOrderId(order.id);
    try {
      const kotText = getKotText(order);
      await printKotViaBluetooth(kotText);
      showSuccessToast('KOT sent to Bluetooth printer', 'kot-bluetooth');
    } catch (err) {
      const message =
        err?.message ||
        (err?.name === 'NotFoundError'
          ? 'No printer selected'
          : 'Bluetooth print failed');
      showErrorToast(message, 'kot-bluetooth');
    } finally {
      setBluetoothPrintingOrderId(null);
    }
  };

  const KotModal = ({ order, onClose }) => {
    const handlePrint = () => {
      window.print();
    };

    const handleDownload = () => {
      // Create a downloadable text version
      const kotContent = `
KITCHEN ORDER TICKET
====================

KOT Reference: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }
Order ID: #${order.id}
Date: ${new Date(order.created_at).toLocaleString()}
${order.order_method === 'Schedule' ? `Scheduled Time: ${order.scheduled_time || 'N/A'}` : ''}

CUSTOMER DETAILS
----------------
Customer ID: ${order.user_id}
Order Type: ${order.order_type.toUpperCase()}
Order Method: ${order.order_method}
${order.order_type === 'delivery' ? `Delivery Address: ${order.delivery_address || 'Not provided'}` : ''}

ORDER ITEMS
-----------
${
  order.orderItems
    ?.map((item) => {
      let itemText = `${item.quantity}x ${
        item.menuItem?.name || `Item #${item.menu_item_id}`
      } - £${item.price}`;
      if (item.addons && item.addons.length > 0) {
        itemText += '\n  Add-ons:';
        item.addons.forEach((addon) => {
          itemText += `\n    ${addon.quantity}x ${
            addon.menuItemAddon?.name || `Addon #${addon.menu_item_addon_id}`
          } - £${addon.price_at_time}`;
        });
      }
      return itemText;
    })
    .join('\n') || 'No items'
}

SPECIAL INSTRUCTIONS
--------------------
${order.delivery_instructions || 'None'}

ALLERGY INFORMATION
-------------------
${order.allergy_note || 'None'}

TOTAL: £${order.total_amount}
Payment: ${
        order.payment_method === 'cash_at_counter'
          ? 'Cash at Counter'
          : 'Card Payment'
      }
Status: ${order.status.toUpperCase()}

Generated: ${new Date().toLocaleString()}
      `.trim();

      const blob = new Blob([kotContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KOT_${
        order.kot_reference ||
        order.kotReference ||
        order.kot?.reference ||
        order.id
      }.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const handleShare = async () => {
      const kotText = `KOT: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }\nOrder #${order.id}\nTotal: £${order.total_amount}\nStatus: ${
        order.status
      }${order.order_method === 'Schedule' ? `\nScheduled: ${order.scheduled_time}` : ''}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }`,
            text: kotText,
          });
        } catch (err) {
          console.log('Error sharing:', err);
        }
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(kotText).then(() => {
          showSuccessToast('KOT details copied to clipboard!', 'kot-share');
        });
      }
    };

    const handleQuickPrint = () => {
      // Auto-print without opening print dialog
      const printWindow = window.open('', '_blank');
      const kotContent = `
        <html>
          <head>
            <title>KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }</title>
            <style>
              body { font-family: monospace; margin: 20px; }
              .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
              .item { margin: 10px 0; padding: 5px 0; }
              .addon { margin-left: 20px; font-size: 0.9em; }
              .total { font-weight: bold; font-size: 1.2em; text-align: center; margin-top: 20px; }
              .footer { text-align: center; margin-top: 20px; font-size: 0.8em; }
              .schedule-info { background-color: #f0f8ff; padding: 10px; margin: 10px 0; border-left: 4px solid #4169e1; }
              .address-section { background-color: #f0fff0; padding: 10px; margin: 10px 0; border-left: 4px solid #32CD32; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>KITCHEN ORDER TICKET</h1>
              <p><strong>KOT:</strong> ${
                order.kot_reference ||
                order.kotReference ||
                order.kot?.reference
              } | <strong>Order:</strong> #${order.id}</p>
              <p>${new Date(order.created_at).toLocaleString()}</p>
              ${
                order.order_method === 'Schedule'
                  ? `<p><strong>Scheduled Order:</strong> ${order.scheduled_time || 'N/A'}</p>`
                  : ''
              }
            </div>
            
            <div class="item">
              <strong>Customer:</strong> ${
                order.user_id
              } | <strong>Type:</strong> ${order.order_type.toUpperCase()} | <strong>Method:</strong> ${
                order.order_method
              }
            </div>
            
            ${
              order.order_method === 'Schedule'
                ? `
                <div class="schedule-info">
                  <strong>⏰ Scheduled Time:</strong> ${order.scheduled_time || 'N/A'}<br>
                  <strong>📅 Scheduled Date:</strong> ${
                    order.scheduled_date
                      ? new Date(order.scheduled_date).toLocaleDateString()
                      : 'N/A'
                  }
                </div>
              `
                : ''
            }
            
            ${
              order.order_type === 'delivery' && order.delivery_address
                ? `
                <div class="address-section">
                  <strong>📍 Delivery Address:</strong><br>
                  ${order.delivery_address}
                  ${
                    order.delivery_instructions
                      ? `<br><strong>Instructions:</strong> ${order.delivery_instructions}`
                      : ''
                  }
                </div>
              `
                : ''
            }
            
            <h3>ORDER ITEMS:</h3>
            ${
              order.orderItems
                ?.map(
                  (item) => `
              <div class="item">
                <strong>${item.quantity}x</strong> ${
                    item.menuItem?.name || `Item #${item.menu_item_id}`
                  } - £${item.price}
                ${
                  item.addons && item.addons.length > 0
                    ? `
                  ${item.addons
                    .map(
                      (addon) => `
                    <div class="addon">+ ${addon.quantity}x ${
                        addon.menuItemAddon?.name ||
                        `Addon #${addon.menu_item_addon_id}`
                      } - £${addon.price_at_time}</div>
                  `
                    )
                    .join('')}
                `
                    : ''
                }
              </div>
            `
                )
                .join('') || 'No items'
            }
            
            ${
              order.allergy_note
                ? `
              <div class="item">
                <strong>⚠️ Allergy Info:</strong> ${order.allergy_note}
              </div>
            `
                : ''
            }
            
            <div class="total">
              <strong>TOTAL: £${order.total_amount}</strong><br>
              Payment: ${
                order.payment_method === 'cash_at_counter'
                  ? 'Cash at Counter'
                  : 'Card Payment'
              }
            </div>
            
            <div class="footer">
              Generated: ${new Date().toLocaleString()}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(kotContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Receipt className="h-8 w-8" />
                <div>
                  <h2 className="text-xl font-bold">Kitchen Order Ticket</h2>
                  <p className="text-green-100">
                    KOT Reference:{' '}
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                    {order.order_method === 'Schedule' && ' (Scheduled)'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleQuickPrint}
                  className="p-2 bg-green-500 hover:bg-green-400 rounded-lg transition-colors"
                  title="Quick Print"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleOrderPrintBluetooth(order)}
                  disabled={bluetoothPrintingOrderId === order.id}
                  className="p-2 bg-blue-500 hover:bg-blue-400 rounded-lg transition-colors disabled:opacity-50"
                  title="Bluetooth Print"
                >
                  {bluetoothPrintingOrderId === order.id ? (
                    <span className="h-5 w-5 block animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z"/></svg>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="text-green-100 hover:text-white p-1 rounded"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* KOT Content */}
          <div className="p-6" id="kot-content">
            {/* Order Header */}
            <div className="border-b border-secondary-200 pb-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-bold text-lg text-secondary-900">
                    Order #{order.id}
                  </h3>
                  <p className="text-secondary-600">
                    <Clock className="inline h-4 w-4 mr-1" />
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                  {order.order_method === 'Schedule' && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-blue-800">
                        <CalendarClock className="h-4 w-4" />
                        <div>
                          <p className="font-medium">Scheduled Order</p>
                          {order.scheduled_date && (
                            <p className="text-sm">
                              Date: {new Date(order.scheduled_date).toLocaleDateString()}
                            </p>
                          )}
                          {order.scheduled_time && (
                            <p className="text-sm">
                              Time: {order.scheduled_time}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-left md:text-right">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                    <span className="mr-1">📋</span>
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                  </div>
                  <p className="text-sm text-secondary-600 mt-1">
                    Customer ID: {order.user_id}
                  </p>
                  <p className="text-sm text-secondary-600">
                    Order Method: {order.order_method}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Type & Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Order Type
                </h4>
                <div className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg capitalize">
                    {order.order_type.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Payment Method
                </h4>
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg">
                    {order.payment_method === 'cash_at_counter'
                      ? 'Cash at Counter'
                      : 'Card Payment'}
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Address Section */}
            {order.order_type === 'delivery' && order.delivery_address && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Delivery Address
                </h4>
                <div className="bg-white p-3 rounded-lg border border-green-100">
                  <p className="text-green-800 font-medium">{order.delivery_address}</p>
                  {order.delivery_instructions && (
                    <div className="mt-2 pt-2 border-t border-green-100">
                      <p className="text-sm text-green-700">
                        <strong>Delivery Instructions:</strong> {order.delivery_instructions}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vehicle Information - Add this new section for KOT */}
            {order.vehicle_data && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <Car className="h-5 w-5 mr-2" />
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Make
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.make}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Colour
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.colour}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Registration
                    </p>
                    <p className="font-bold text-blue-900 font-mono text-lg">
                      {order.vehicle_data.registrationNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="mb-6">
              <h4 className="font-bold text-lg text-secondary-900 mb-3 border-b border-secondary-200 pb-2">
                Order Items ({order.orderItems?.length || 0})
              </h4>
              <div className="space-y-3">
                {order.orderItems && order.orderItems.length > 0 ? (
                  order.orderItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-secondary-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm print-break"
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="bg-green-600 text-white text-sm font-bold px-2 py-1 rounded-full min-w-[24px] text-center">
                              {item.quantity}
                            </span>
                            <h5 className="font-bold text-lg text-secondary-900">
                              {item.menuItem?.name ||
                                `Item #${item.menu_item_id}`}
                            </h5>
                            {item.menuItem?.category_name && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {item.menuItem.category_name}
                              </span>
                            )}
                          </div>
                          {item.menuItem?.description && (
                            <p className="text-sm text-secondary-600 ml-8 mb-2">
                              {item.menuItem.description}
                            </p>
                          )}

                          {/* Display addons in KOT view */}
                          {item.addons && item.addons.length > 0 && (
                            <div className="ml-8 bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                              <p className="text-sm font-bold text-blue-900 mb-2">
                                🍽️ Add-ons:
                              </p>
                              <div className="space-y-1">
                                {item.addons.map((addon, addonIndex) => (
                                  <div
                                    key={`${addon.id}-${addonIndex}`}
                                    className="flex justify-between items-center text-sm"
                                  >
                                    <div className="text-blue-800">
                                      <span className="font-bold">
                                        {addon.quantity}x
                                      </span>
                                      <span className="ml-2">
                                        {addon.menuItemAddon?.name ||
                                          `Addon #${addon.menu_item_addon_id}`}
                                      </span>
                                    </div>
                                    <div className="text-blue-900 font-medium">
                                      £
                                      {(
                                        addon.quantity *
                                        parseFloat(addon.price_at_time)
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.special_instructions && (
                            <div className="ml-8 bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                              <p className="text-sm text-yellow-800">
                                <strong>Special Instructions:</strong>{' '}
                                {item.special_instructions}
                              </p>
                            </div>
                          )}
                          {item.allergy_information && (
                            <div className="ml-8 bg-red-50 border border-red-200 rounded p-2 mt-2">
                              <p className="text-sm text-red-800">
                                <strong>⚠️ Allergy Info:</strong>{' '}
                                {item.allergy_information}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-left md:text-right ml-8 md:ml-4 mt-2 md:mt-0">
                          <p className="font-bold text-lg text-secondary-900">
                            £
                            {item.total_price
                              ? parseFloat(item.total_price).toFixed(2)
                              : (
                                  item.quantity * parseFloat(item.price)
                                ).toFixed(2)}
                          </p>
                          <p className="text-sm text-secondary-600">
                            £{item.price} base price
                          </p>
                          {item.addons && item.addons.length > 0 && (
                            <p className="text-xs text-green-600 font-medium">
                              + add-ons included
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-secondary-500">
                    No items found for this order
                  </div>
                )}
              </div>
            </div>

            {/* Special Instructions & Allergies */}
            {(order.delivery_instructions || order.allergy_note) && (
              <div className="space-y-3 mb-6 print-break">
                {order.delivery_instructions && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      📝 Special Instructions
                    </h4>
                    <p className="text-blue-800">
                      {order.delivery_instructions}
                    </p>
                  </div>
                )}
                {order.allergy_note && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2">
                      ⚠️ Allergy Information
                    </h4>
                    <p className="text-red-800">{order.allergy_note}</p>
                  </div>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-secondary-100 p-4 rounded-lg border border-secondary-200 print-break">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-secondary-900">
                  Order Total:
                </span>
                <span className="font-bold text-2xl text-secondary-900">
                  £{order.total_amount}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">
                  Payment Status:
                </span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    order.payment_status === 'paid'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {order.payment_status}
                </span>
              </div>
            </div>

            {/* Order Status */}
            <div className="text-center mt-4 print-break">
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full font-medium ${getStatusColor(
                  order.status
                )}`}
              >
                Status: {order.status.toUpperCase().replace('_', ' ')}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-secondary-50 p-4 rounded-b-lg border-t border-secondary-200">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
              <div className="text-sm text-secondary-600 text-center md:text-left">
                Generated: {new Date().toLocaleString()}
              </div>
              <div className="flex flex-wrap justify-center md:justify-end gap-2">
                <button
                  onClick={handleShare}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center space-x-2 px-3 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          Failed to load orders
        </h3>
        <p className="text-secondary-600 mb-4">
          {error.response?.data?.message ||
            error.message ||
            'An error occurred while loading orders'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Handle case when orders is null or undefined
  if (!orders) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-secondary-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          No orders data
        </h3>
        <p className="text-secondary-600">Please try refreshing the page</p>
      </div>
    );
  }

  const scheduledOrdersCount = filteredOrders.filter(order => order.order_method === 'Schedule').length;

  return (
    <div className=" ">
      {/* Header with Download/Print buttons for Scheduled Orders */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Orders</h1>
          <p className="text-secondary-600">
            Manage and track incoming customer orders
          </p>
        </div>
        
        {orderMethod === 'Schedule' && scheduledOrdersCount > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleScheduledOrderDownload}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              title="Download scheduled orders as Excel/CSV"
            >
              <FileSpreadsheet className="h-4 w-4" />
              <span>Download CSV ({scheduledOrdersCount})</span>
            </button>
            <button
              onClick={handleScheduledOrderPrint}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title="Print scheduled orders report"
            >
              <Printer className="h-4 w-4" />
              <span>Print Report</span>
            </button>
          </div>
        )}
      </div>

      {/* Order count header */}
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-secondary-200">
        <Package className="h-5 w-5 text-primary-600" />
        <span className="font-semibold text-secondary-900">All Orders ({orders.length})</span>
      </div>

      {/* Search and Filters - Tablet Optimized */}
      <div className="bg-white rounded-lg  border border-secondary-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-secondary-900">
            Search & Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-700 underline self-start md:self-auto"
            >
              Clear all filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search orders, customers, items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full text-sm md:text-base"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Status</option>
            <option value="pending">⏳ Pending</option>
            <option value="pending_approval">🔍 Pending Approval</option>
            <option value="confirmed">✅ Confirmed</option>
            <option value="preparing">👨‍🍳 Preparing</option>
            <option value="ready">🎉 Ready</option>
            <option value="out_for_delivery">🚚 Out for Delivery</option>
            <option value="completed">✅ Completed</option>
            <option value="cancelled">❌ Cancelled</option>
          </select>

          <select
            value={orderTypeFilter}
            onChange={(e) => setOrderTypeFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Order Types</option>
            <option value="dine_in">🍽️ Dine-in</option>
            <option value="takeaway">📦 Takeaway</option>
            <option value="delivery">🚚 Delivery</option>
          </select>

          <select
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Payment Methods</option>
            <option value="stripe">💳 Card Payment</option>
            <option value="cash_at_counter">💵 Cash at Counter</option>
            <option value="apple_pay">🍎 Apple Pay</option>
            <option value="google_pay">🤖 Google Pay</option>
          </select>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="">All Dates</option>
              <option value="today">📅 Today</option>
              <option value="yesterday">📅 Yesterday</option>
              <option value="week">📅 This Week</option>
              <option value="month">📅 This Month</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="date">📅 Sort by Date</option>
              <option value="total">💰 Sort by Total</option>
              <option value="id">🆔 Sort by Order ID</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-sm md:text-base"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-secondary-600">
              📊 Showing {filteredOrders.length} of {orders.length} orders
              {orderMethod !== 'all' && ` (${orderMethod} only)`}
            </span>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="flex items-center space-x-2 mb-4">
        <button
          onClick={() =>
            updateNotificationSettings({ sound: !notificationSettings.sound })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.sound
              ? 'bg-green-100 text-green-600 hover:bg-green-200'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          title={notificationSettings.sound ? 'Sound On' : 'Sound Off'}
        >
          {notificationSettings.sound ? (
            <Bell className="h-5 w-5" />
          ) : (
            <BellOff className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={() =>
            updateNotificationSettings({
              browser: !notificationSettings.browser,
            })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.browser
              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          title={
            notificationSettings.browser
              ? 'Browser Notifications On'
              : 'Browser Notifications Off'
          }
        >
          {notificationSettings.browser ? (
            <Smartphone className="h-5 w-5" />
          ) : (
            <Smartphone className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Orders List - Tablet Optimized */}
      <div className="space-y-3 md:space-y-4">
        {filteredOrders.map((order) => {
          const inlineActions = getInlineStatusActions(order);
          const kotActions = getKotActions(order);

          return (
            <div
              key={order.id}
              className="card hover:shadow-md transition-all duration-200 hover-lift"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className={`h-10 w-10 md:h-12 md:w-12 rounded-full flex items-center justify-center ${
                    order.order_method === 'Instant' ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                    {order.order_method === 'Instant' ? (
                      <Zap className="h-5 w-5 md:h-6 md:w-6 text-yellow-600" />
                    ) : (
                      <CalendarClock className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                      <h3 className="font-semibold text-secondary-900 text-sm md:text-base">
                        Order #{order.id}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          order.order_method === 'Instant' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {order.order_method === 'Instant' ? (
                            <>
                              <Zap className="h-3 w-3 mr-1" />
                              Instant
                            </>
                          ) : (
                            <>
                              <CalendarClock className="h-3 w-3 mr-1" />
                              Scheduled
                            </>
                          )}
                        </span>
                        {order.order_method === 'Schedule' && order.scheduled_time && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            <Clock4 className="h-3 w-3 mr-1" />
                            {order.scheduled_time}
                          </span>
                        )}
                        {(() => {
                          const kotReference =
                            order.kot_reference ||
                            order.kotReference ||
                            order.kot?.reference;
                          return (
                            kotReference && (
                              <span className="font-mono text-xs md:text-sm font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded flex items-center space-x-1">
                                <span>📋</span>
                                <span>KOT: {kotReference}</span>
                              </span>
                            )
                          );
                        })()}
                      </div>
                    </div>
                    <p className="text-xs md:text-sm text-secondary-600">
                      User #{order.user_id} • {order.orderItems?.length || 0}{' '}
                      items • {order.order_type}
                    </p>
                    <p className="text-xs text-secondary-500">
                      📅 {new Date(order.created_at).toLocaleString()}
                      {order.order_method === 'Schedule' && order.scheduled_date && (
                        <span className="ml-2 text-blue-600">
                          ⏰ Scheduled: {new Date(order.scheduled_date).toLocaleDateString()} {order.scheduled_time}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="text-left sm:text-right">
                    <p className="font-bold text-secondary-900 text-sm md:text-base">
                      💰 £{order.total_amount}
                    </p>
                    <span
                      className={`inline-flex items-center px-2 py-1 md:px-2.5 md:py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {(() => {
                        const flow = getStatusFlow(order);
                        const currentStatus = flow.flow.find(
                          (s) => s.status === order.status
                        );
                        return currentStatus
                          ? `${currentStatus.icon} ${currentStatus.label}`
                          : order.status;
                      })()}
                    </span>
                    <p className="text-xs text-secondary-500 mt-1">
                      {order.payment_method === 'cash_at_counter'
                        ? '💵 Cash'
                        : '💳 Card'}
                    </p>
                  </div>

                  {/* Enhanced Inline Actions Section */}
                  <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
                    {/* Status Actions */}
                    {inlineActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {inlineActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={
                              action.disabled ? undefined : action.action
                            }
                            disabled={action.loading || action.disabled}
                            className={`
                              group relative flex items-center justify-center space-x-1.5 
                              px-3 py-2 text-white rounded-lg text-xs font-semibold 
                              ${action.color} disabled:opacity-50 
                              transition-all duration-200 hover:shadow-md hover:scale-105
                              active:scale-95 border border-transparent hover:border-white/20
                              min-w-[60px] sm:min-w-[80px]
                              ${action.disabled ? 'cursor-not-allowed' : ''}
                            `}
                            title={
                              action.disabled
                                ? 'This action is only available to admin'
                                : action.label
                            }
                          >
                            {action.loading ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <span className="text-sm">{action.icon}</span>
                                <span className="hidden sm:inline font-medium">
                                  {action.label}
                                </span>
                              </>
                            )}
                            {/* Hover tooltip for mobile */}
                            <div
                              className="absolute -top-8 left-1/2 transform -translate-x-1/2 
                                          bg-gray-900 text-white text-xs px-2 py-1 rounded 
                                          opacity-0 group-hover:opacity-100 transition-opacity
                                          pointer-events-none whitespace-nowrap z-10 sm:hidden"
                            >
                              {action.disabled ? 'Admin Only' : action.label}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Bluetooth Print Action */}
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => handleOrderPrintBluetooth(order)}
                        disabled={bluetoothPrintingOrderId === order.id}
                        className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-all duration-200 hover:shadow-md hover:scale-105 active:scale-95 border border-transparent hover:border-white/20 min-w-[60px] sm:min-w-[80px]"
                        title="Bluetooth Print"
                      >
                        {bluetoothPrintingOrderId === order.id ? (
                          <span className="h-3 w-3 block animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z"/></svg>
                        )}
                        <span className="hidden sm:inline">Print</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-2 self-start sm:self-auto">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="p-2 text-secondary-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      title="View Order Details"
                    >
                      <Eye className="h-4 w-4 md:h-5 md:w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {searchTerm || statusFilter || orderMethod !== 'all' ? 'No orders found' : 'No orders yet'}
          </h3>
          <p className="text-secondary-600">
            {searchTerm || statusFilter || orderMethod !== 'all'
              ? 'Try adjusting your search terms or filters'
              : 'Orders will appear here when customers place them'}
          </p>
          {orderMethod !== 'all' && (
            <button
              onClick={() => setOrderMethod('all')}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Show All Orders
            </button>
          )}
        </div>
      )}

      {/* Order Modal */}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          setKotOrder={setKotOrder}
          setShowKotView={setShowKotView}
          getStatusColor={getStatusColor}
          getStatusFlow={getStatusFlow}
          getStatusAction={getStatusAction}
          updateOrderStatusMutation={updateOrderStatusMutation}
          onCancelOrder={(orderId) => {
            setCancelOrderId(orderId);
            setCancelReason('');
            setShowCancelModal(true);
            setSelectedOrder(null);
          }}
        />
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Cancel Order #{cancelOrderId}</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select a reason...</option>
                <option value="Sorry, the item is currently unavailable">Sorry, the item is currently unavailable</option>
                <option value="We're unable to process your order at the moment">We're unable to process your order at the moment</option>
                <option value="Due to high demand, this order couldn't be completed">Due to high demand, this order couldn't be completed</option>
                <option value="Store is temporarily unavailable">Store is temporarily unavailable</option>
                <option value="Unexpected stock shortage">Unexpected stock shortage</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelOrderId(null);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
              >
                Go Back
              </button>
              <button
                onClick={() => {
                  if (!cancelReason.trim()) {
                    showErrorToast('Cancellation reason is required', 'cancel-reason');
                    return;
                  }
                  updateOrderStatusMutation.mutate({
                    orderId: cancelOrderId,
                    status: 'cancelled',
                    reason: cancelReason.trim(),
                  });
                  setShowCancelModal(false);
                  setCancelOrderId(null);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KOT Modal */}
      {showKotView && kotOrder && (
        <KotModal
          order={kotOrder}
          onClose={() => {
            setShowKotView(false);
            setKotOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default Orders;